/**
 * Generates a toolbar of buttons given a config containing a container element
 * and a collection of button options. Buttons contain an icon, action, and
 * visibility.
 *
 * ## Options:
 * - `class`: The class for the toolbar instance
 * - `container`: The element (selector or element) to append the toolbar
 * - `buttons`: A function *returning* a collection containing the buttons for
 *    your toolbar.
 *	+ `buttons.icon`: The icon name
 *	+ `buttons.label`: The label name
 *  + `buttons.class`: The classnames to add to the button
 *  + `buttons.visibility`: An array of booleans (ie [someFunction(), isTrue()])
 *    that if any are `false` (must be false, NOT "falsy") the button will not
 *    be rendered.
 *  + `buttons.action`: A function that is passed to the jQuery on click handler
 *		that is fired when the button is clicked.
 *	+ `buttons.options`: an	array of option objects
 *	 * `buttons.options.text` : the text that appears on the option button
 *	 * `buttons.options.class` : the classname of option buton
 *	 * `buttons.options.action`: A function that is passed to the jQuery on click handler
 *		that is fired when the option button is clicked.
 *
 * ## Methods
 * - hide(): hides the toolbar (sets display:none)
 * - show(): shows the toolbar (sets display:block)
 * - render(): Re-renders the toolbar
 *
 * ## Grouping
 * You can also nest your buttons one level deep like Adobe app does with it's
 * toolbars. For example, say this is your buttons array:
 * [
 *   {
 *     'Tool 1': {}
 *   },
 *   [
 *     {
 *       'Tool 2.a': {}
 *     },
 *     {
 *       'Tool 2.b': {}
 *     },
 *   ]
 * ]
 *
 * The toolbar will look like:
 *
 * [ Tool 1  |  Tool 2.a ]
 *
 * Once you hover over 2.a it'll drop down like this:
 *
 * [ Tool 1  |  Tool 2.a ]
 * [         |  Tool 2.a ]
 * [         |  Tool 2.b ]
 *
 * If you click on 2.b it becomes
 *
 * [ Tool 1  |  Tool 2.b ]
 *
 * ## Why is buttons a function?!
 * Buttons is a function that returns a collection so that it can be rerendered
 * without having a cached state object.
 *
 * Say you have a function like `isAdvanced` and on initial load advaned is
 * false so you have visibility: [isAdvanced()] which returns
 * visibility: [false]. Now if you go to re-render it, it'll still be
 * visibility: [false]. With buttons as a function it can reexecute all the
 * options to make sure the state isn't cached.
 *
 * ## Example setup:
 *  new BatchGeoToolbar({
 *		container: '#mapWrap',
 *		buttons: function () {
 *			return [
 *				{
 *					// The title of the button. Shows in the `title=""` on the button
 *					'Reset map': {
 *	          // creates a <span class="toolbar-icon-refresh"> element
 *						'icon': 'refresh',
 *
 *						// Adds "mapReset" to the class of the button. The button will look
 *						// like: <button class="batchgeo-toolbar-button mapReset">
 *						'class': 'mapReset',
 *
 *						// The function to call when this button is clicked on. It is a
 *						// jQuery event so you can do $(this) to get the button
 *						'action': function (event) {
 *							window.mapReset(event);
 *						}
 *					}
 *				},
 *				// Creates the "group" that, when hovered, shows all options inside.
 *				// When not hovering it only shows the first item. Once a sub item is
 *				// click the first toolbar button is updated with the active one.
 *				[
 *					{
 *						'Rectangle selection tool': {
 *							'icon': 'rectangle',
 *							'class': 'drawing-manager-button-rectangle',
 *							// visibility lets you pass in booleans and if ANY are false it
 *							// will not render the button at all
 *							'visibility': [!isMobile(), isPro()],
 *							'action': function () {
 *								// ...
 *							}
 *						}
 *					}
 *				]
 *			}
 *		}
 *  });
 */
(function (window) {
  function BatchGeoToolbar(opts) {
    this.settings = $.extend(
      {
        class: "", //The class for the toolbar,
        container: null, // The element to append the toolbar into
        buttons: function () {
          return [];
        }, // The array of buttons for the toolbar
      },
      opts,
    );
    this.toolbarElement = $("<div />", {
      class: "batchgeo-toolbar " + opts["class"],
    });
    this.toolbarTooltip = $("<span />", { class: "batchgeo-toolbar-tooltip" });
    // Add the newly created toolbar element
    this.toolbarElement.appendTo(opts.container);
    this.toolbarTooltip.appendTo(opts.container);
    this.render();
  }

  BatchGeoToolbar.prototype.render = function () {
    this.toolbarElement.empty();
    this._renderButtons();
  };

  BatchGeoToolbar.prototype._createButton = function (buttonName, buttonOpts) {
    var buttonSettings = $.extend(
      {
        icon: "", // the icon to use for the button
        label: "",
        class: "", // will be put directly into class="HERE"
        action: function () {}, // what to fire when you click the button
        options: [],
      },
      buttonOpts,
    );
    var $icon = $("<span />", {
      class: "batchgeo-toolbar-button-icon toolbar-icon-" + buttonSettings.icon,
      "aria-hidden": true,
    });

    var $options = $("<div />", {
      class: "toolbar-options-button-group",
      html: buttonSettings.options.map(function (option) {
        return $("<button />", {
          addClass:
            "batchgeo-toolbar-button " +
            "batchgeo-" +
            buttonSettings.icon +
            "-options-button-" +
            option["class"],
          html: option.text.toUpperCase(),
          title: "Switch to " + option["class"],
          click: option.action,
        });
      }),
    });

    var $label = $("<span />", {
      html: buttonSettings.label,
    });

    var optionsClass = buttonSettings.options.length > 0 ? " has-options" : "";
    var $button = $("<button />", {
      class:
        "batchgeo-toolbar-button " + buttonSettings["class"] + optionsClass,
      title: buttonName,
    });

    $button.append([$icon, $label]);
    if (buttonSettings.options.length > 0) {
      $button.append($options);
    }
    $button.on("click", buttonSettings.action);

    return $button;
  };

  BatchGeoToolbar.prototype._addButtonInGroup = function (group) {
    var button;
    var buttonVisibility;

    for (const buttonName in group) {
      buttonVisibility = group[buttonName].visibility;
      // If there was no visibility filters set OR none of them are falsy we
      // then can create the button
      if (
        buttonVisibility == undefined ||
        buttonVisibility.indexOf(false) == -1
      ) {
        button = this._createButton(buttonName, group[buttonName]);
        return button;
      }
    }
  };

  BatchGeoToolbar.prototype._renderButtons = function () {
    this.settings.buttons().forEach(function (buttonGroup) {
      var buttons = [];
      var $buttonGroup = $("<div />", {
        class: "batchgeo-toolbar-button-group",
      });

      // If the button group is not an object and it's an array of more groups
      // we loop through these differently so we can make a dropdown
      if (buttonGroup instanceof Array) {
        // We add two of the same buttons and we always swap out the first when
        // one of the sub items is clicked
        buttons.push(this._addButtonInGroup(buttonGroup[0]));
        buttonGroup.forEach(function (button) {
          buttons.push(this._addButtonInGroup(button));
        }, this);
      } else if (buttonGroup.inlineGroup) {
        $buttonGroup.addClass(buttonGroup.class);

        var firstButton = 0;
        if (buttonGroup.headerTitle) {
          var header = $("<div>" + buttonGroup.headerTitle + "</div>", {
            class: buttonGroup.style,
          });
          header.addClass(buttonGroup.headerStyle);
          buttons.push(header);
          firstButton++;
        }

        buttonGroup.buttons.forEach((element) => {
          var button = this._addButtonInGroup(element);
          if (!button) return;
          button.addClass(buttonGroup.style);
          button.addClass(buttonGroup.buttonStyle);

          var spacer = $("<div />", {
            class: buttonGroup.style + " " + buttonGroup.spacerStyle,
          });
          if (button.hasClass("grey-out")) spacer.addClass("grey-out");
          if (buttons.length > firstButton) {
            buttons.push(spacer);
          }
          buttons.push(button);
        });
        if (buttons.every((button) => button.hasClass("grey-out")))
          $buttonGroup.addClass("grey-out");
      }
      // If it's a top level button group we can simply add the group to the DOM
      else {
        buttons.push(this._addButtonInGroup(buttonGroup));
      }

      // If the button group returned undefined (because of falsy visibility
      // checks) then the array will contain undefineds. Here we remove the
      // undefined values in our buttons array
      buttons = buttons.filter(function (b) {
        return b;
      });

      // If there was actual buttons returned we can inject them into the DOM
      if (buttons.length > 0) {
        $buttonGroup.append(buttons);
        this.toolbarElement.append($buttonGroup);
      }
    }, this);
  };

  BatchGeoToolbar.prototype.hide = function () {
    this.toolbarElement.hide();
  };

  BatchGeoToolbar.prototype.show = function () {
    this.toolbarElement.show();
  };

  BatchGeoToolbar.prototype.showTooltip = function (content) {
    var self = this;
    clearTimeout(this.tooltipTimeout);
    this.toolbarTooltip.text(content).show(0, function () {
      self.tooltipTimeout = setTimeout(function () {
        BatchGeoStore.dispatch({ type: "RESET_TOOLBAR_TOOLTIP" });
      }, 3000);
    });
  };

  BatchGeoToolbar.prototype.hideTooltip = function () {
    this.toolbarTooltip.hide();
  };

  window.BatchGeoToolbar = BatchGeoToolbar;
})(window);
